import React, { useState, useCallback, memo, useMemo, Suspense, useRef, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Avatar, Badge, IconButton, Link, useTheme, Box, Typography, Tooltip, alpha, Dialog, DialogContent, CircularProgress } from '@mui/material';
import { AnalyticsOutlined, CalendarMonthOutlined, ExploreOutlined, GridViewOutlined, NotificationsOutlined, PieChartOutline, InfoOutlined, Close as CloseIcon } from '@mui/icons-material';
import Stack from 'components/mui/Stack';
import AppBoxComponent from 'components/mui/AppBoxComponent';
import NavigationTabsComponent from 'components/layouts/NavigationTabsComponent';
import ProfileMenuComponent from 'components/layouts/ProfileMenuComponent';
import { getInitials } from 'utils/string.utils';
import ROUTES from 'router/routes.constants';
import { getActiveProfileTabIndex } from 'src/constants/profile-tabs.constants';
import Logo from 'assets/images/logo.svg';
import { LoadingFallback } from 'components/common';
import exploreBannerVideo from 'assets/video/exploreBanner.mp4';
import AppButtonComponent from 'components/mui/AppButtonComponent';
import ServiceRequestContainer from 'containers/ServiceRequestContainer';
import ApiHistoryDrawer from 'components/common/ApiHistoryDrawer';
import BreadcrumbNavigation from 'components/common/BreadcrumbNavigation';
import { useGetNotificationCountsQuery } from 'store/api/notifications.apislice';
import { useGetConsentsQuery } from 'store/api/finfactor.apislice';
import PullToRefreshComponent from 'components/mobile/PullToRefreshComponent';
import { baseApi } from 'store/api/baseApi';
import { useDispatch } from 'react-redux';
import { isMobile, isNative, isWeb } from 'utils/platform.utils';
import { useGetOnboardingStatusQuery } from 'store/api/customer.apislice';
import { useUpdateCustomerTypeMutation } from 'store/api/mutualFunds.apislice';
import ChooseExperienceModal from 'components/ChooseExperienceModal';
import toast from 'react-hot-toast';
import { useSubscriptionPayment } from 'hooks/useSubscriptionPayment';
import {
  applyDirectJourneyStepAction,
  ONBOARDING_PENDING_MODAL_EVENT,
} from 'utils/directJourney.utils';
import DigioAgreementModal from 'components/DigioAgreementModal';
import RiskAssessmentModal from 'components/common/RiskAssessmentModal';
import OnboardingPendingModal from 'components/common/modal/OnboardingPendingModal';
import { useGetCurrentSubscriptionQuery } from 'store/api/subscription.apislice';
import {
  getCustomerType,
  getInvestmentEligibility,
  isOnboardingComplete,
  isSubscriptionFree,
  isUccOnboardingComplete,
  getPendingOnboardingStep,
  getOnboardingStepsProgress,
} from 'utils/investmentEligibility.utils';
import { useGetPortfolioHoldingsQuery } from 'store/api/portfolio.apislice';

// Component scope - Constants and static data
const NAV_TABS = [
  { label: 'Home', href: ROUTES.EXPLORE, icon: <ExploreOutlined /> },
  { label: 'My Investments', href: ROUTES.INVESTMENTS, icon: <GridViewOutlined /> },
  { label: 'Explore Mutual Funds', href: ROUTES.MUTUAL_FUNDS, icon: <PieChartOutline /> },
  { label: 'My Portfolio', href: ROUTES.PORTFOLIO, icon: <AnalyticsOutlined /> },
];

/** Routes Direct customers may stay on while onboarding is still incomplete. */
const ONBOARDING_JOURNEY_ROUTE_PREFIXES = [
  '/onboarding',
  '/risk-assessment',
  ROUTES?.CREATE_MANDATE ?? '/create-mandate',
];

/** Account areas that stay reachable while onboarding is still in progress. */
const ONBOARDING_EXEMPT_ROUTE_PREFIXES = [
  ROUTES?.PROFILE ?? '/profile',
  '/reports',
  '/help-center',
];

const isOnboardingExemptRoute = (pathname) => {
  if (!pathname || typeof pathname !== 'string') return false;
  return (ONBOARDING_EXEMPT_ROUTE_PREFIXES ?? []).some(
    (prefix) => typeof prefix === 'string' && pathname.startsWith(prefix),
  );
};

const isOnboardingJourneyRoute = (pathname) => {
  if (!pathname || typeof pathname !== 'string') return false;
  return (ONBOARDING_JOURNEY_ROUTE_PREFIXES ?? []).some(
    (prefix) => typeof prefix === 'string' && pathname.startsWith(prefix),
  );
};

// Route groups for active tab detection
const ROUTE_GROUPS = [
  {
    tabIndex: 0, // Explore tab
    routes: [ROUTES.EXPLORE, '/'],
  },
  {
    tabIndex: 1, // Investments tab
    routes: [
      ROUTES.INVESTMENTS,
      ROUTES.FINANCIAL_OVERVIEW,
      ROUTES.INVESTMENT_PORTFOLIO,
      ROUTES.INVESTMENT_PORTFOLIO_DETAILS,
      ROUTES.INVESTMENT_PORTFOLIO_BROKER,
      ROUTES.INVESTMENT_PORTFOLIO_ORDER,
      ROUTES.FINFACTOR_BANKING,
      ROUTES.FINFACTOR_BANK_DETAILS,
      ROUTES.FINFACTOR_INSURANCE,
    ],
    matchPrefix: '/financial-overview', // Matches any /financial-overview/* route
  },
  {
    tabIndex: 2, // MF Universe tab
    routes: [
      ROUTES.MUTUAL_FUNDS,
      ROUTES.MUTUAL_FUNDS_FUND_ID,
    ],
    matchPrefix: '/mutual-funds', // Matches any /mutual-funds/* route
  },
  {
    tabIndex: 3, // Portfolio tab
    routes: [
      ROUTES.PORTFOLIO,
      ROUTES.PORTFOLIO_MUTUAL_FUND,
      ROUTES.PORTFOLIO_MUTUAL_FUND_FUND_ID,
      ROUTES.PORTFOLIO_ORDER,
      ROUTES.PORTFOLIO_ORDER_ORDER_ID,
      ROUTES.PORTFOLIO_SUMMARY,
      ROUTES.SYSTEMATIC_INVESTMENT,
      ROUTES.SIP_ROOT,
      ROUTES.SIP_ACTIVE,
      ROUTES.STP_ROOT,
      ROUTES.STP_ACTIVE,
      ROUTES.SWP_ROOT,
      ROUTES.SWP_ACTIVE,
      ROUTES.SIPS_ACTIVE,
      ROUTES.SIPS_MANDATES,
      ROUTES.SIPS_SUMMARY,
      ROUTES.SIPS_SIP_ID,
    ],
    matchPrefix: ['/portfolio', '/systematic-investment'], // Matches both prefixes
  },
];
// const ROUTE_GROUPS = [
//   {
//     tabIndex: 0, // Explore tab
//     routes: [ROUTES.EXPLORE, '/'],
//   },
//   {
//     tabIndex: 1, // Investments tab
//     routes: [ROUTES.INVESTMENTS],
//   },
//   {
//     tabIndex: 2, // MF Universe tab
//     routes: [
//       ROUTES.MUTUAL_FUNDS,
//       ROUTES.MUTUAL_FUNDS_FUND_ID,
//     ],
//     matchPrefix: '/mutual-funds', // Matches any /mutual-funds/* route
//   },
//   {
//     tabIndex: 3, // Portfolio tab
//     routes: [
//       ROUTES.PORTFOLIO,
//       ROUTES.PORTFOLIO_MUTUAL_FUND,
//       ROUTES.PORTFOLIO_MUTUAL_FUND_FUND_ID,
//       ROUTES.PORTFOLIO_ORDER,
//       ROUTES.PORTFOLIO_ORDER_ORDER_ID,
//       ROUTES.PORTFOLIO_SUMMARY,
//     ],
//     matchPrefix: '/portfolio', // Matches any /portfolio/* route
//   },
//   {
//     tabIndex: 4, // SIPs tab
//     routes: [
//       ROUTES.SIPS,
//       ROUTES.SIPS_ACTIVE,
//       ROUTES.SIPS_SUMMARY,
//       ROUTES.SIPS_SIP_ID,
//     ],
//     matchPrefix: '/sips', // Matches any /sips/* route
//   },
// ];

const LAYOUT_STYLES = {
  container: { minHeight: '100vh' },
  header: {
    flex: '0 0 auto',
    width: 1,
    borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
    backgroundColor: (theme) => theme.palette.custom?.background?.page || '#13172A', // Ensure opaque background
    // paddingTop: 'env(safe-area-inset-top, 0px)', // Handled globally by AppHOC
  },
  headerContent: {
    maxWidth: '1300px',
    width: 1,
    margin: '0 auto',
    // For xs: stack vertically, for sm+: horizontal
    flexDirection: { xs: 'column', sm: 'row' },
    alignItems: { xs: 'stretch', sm: 'center' },
  },
  topRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: 1,
    px: { xs: 1.5, sm: 2 },
    py: { xs: 1.5, sm: 1 },
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    width: { xs: 80, sm: 100 },
    height: { xs: 38, sm: 42 },
    cursor: 'pointer',
    transition: 'opacity 0.2s ease-in-out',
    '&:hover': {
      opacity: 0.8,
    },
    '& img': {
      maxHeight: { xs: '32px', sm: '36px' },
      width: 'auto',
      objectFit: 'contain',
      display: 'block',
      marginTop: '1px' // Precise shift to align baseline with tab text
    }
  },
  spacer: { flex: '1 1 auto' },
  navTabsContainer: {
    width: 1,
    // Show stacked/scrollable for xs/sm (up to md), then hide
    display: { xs: 'block', md: 'none' },
  },
  navTabsInline: {
    // Show inline with logo from md+ screens
    display: { xs: 'none', md: 'flex' },
    flex: '1 1 auto',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  mainContent: {
    flex: '1 1 auto',
    width: 1,
    maxWidth: '1300px',
    margin: '0 auto',
    // paddingBottom: 'env(safe-area-inset-bottom, 0px)', // Handled globally by AppHOC
  },
  exploreContentWrapper: {
    position: 'relative',
    flex: '1 1 auto',
    width: 1,
    maxWidth: '100%',
    margin: 0,
    overflow: 'hidden',
    backgroundColor: (theme) => theme.palette.custom?.background?.page || '#13172A',
  },
  exploreBackgroundVideo: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    zIndex: 0,
    filter: 'brightness(0.65)', display: 'block',
  },
  exploreContentOverlay: {
    position: 'relative',
    zIndex: 1,
    flex: '1 1 auto',
    width: 1,
    maxWidth: '1300px',
    margin: '0 auto',
    px: { xs: 1.5, sm: 2 },
    pt: { xs: 3, sm: 4 },
    pb: { xs: '24px', sm: '32px' }, // Modified to remove double safe area
  },
};

/**
 * Main Layout Component - Main application layout with navigation and profile menu
 * @component
 * @returns {React.ReactElement} MainLayout
 */
const MainLayout = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // States, refs, selectors, RTK Query hooks
  // States, refs, selectors, RTK Query hooks
  const { data: countsData, refetch: refetchNotifications } = useGetNotificationCountsQuery(undefined, {
    // NOTE: refetchOnMountOrArgChange only triggers on mount or argument change.
    // It does NOT respond to tag invalidation automatically if the component is already mounted.
    // Tag invalidation (invalidateTags) is the correct mechanism for Pull-to-Refresh.
    refetchOnMountOrArgChange: true,
  });
  const { data: consentsData, refetch: refetchConsents } = useGetConsentsQuery();

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [showChooseExperience, setShowChooseExperience] = useState(false);
  const [showAgreement, setShowAgreement] = useState(false);
  const [showRiskAssessment, setShowRiskAssessment] = useState(false);
  // Set from the invest gate: { pendingStep, incompleteSteps, actionType }.
  const [onboardingPendingPrompt, setOnboardingPendingPrompt] = useState(null);
  const hasAgreementBeenDismissed = useRef(false);
  const hasRiskBeenDismissed = useRef(false);
  const hasClosedPlanModal = useRef(false);
  const isJourneyModalOpenRef = useRef(false);
  const suppressJourneyEnforcementUntilRef = useRef(0);
  const user = useSelector(state => state.user?.profile);

  const markJourneyModalDismissed = useCallback(() => {
    suppressJourneyEnforcementUntilRef.current = Date.now() + 400;
  }, []);

  // Global Onboarding checking
  const { data: onboardingData, isLoading: isLoadingOnboarding, refetch: refetchOnboarding } = useGetOnboardingStatusQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const [updateCustomerType] = useUpdateCustomerTypeMutation();
  const { data: subscriptionResponse, isLoading: isSubLoading, isFetching: isSubFetching } = useGetCurrentSubscriptionQuery();
  const isPaidSubscription =
    subscriptionResponse?.result?.status === 'ACTIVE' ||
    subscriptionResponse?.result?.current?.status === 'ACTIVE';
  const eligibility = getInvestmentEligibility(onboardingData, {
    isSubscriptionActive: isPaidSubscription,
  });
  const customerType = eligibility.customerType;
  const isFreeCustomer = eligibility.isFreeCustomer;
  const isSubscriptionActive =
    isPaidSubscription ||
    customerType === 'REGULAR' ||
    isFreeCustomer;

  const {
    initiatePayment,
    isVerifying,
    verificationMessage,
    cancelVerification,
    paymentUrl
  } = useSubscriptionPayment();

  useEffect(() => {
    isJourneyModalOpenRef.current =
      showChooseExperience ||
      showAgreement ||
      showRiskAssessment ||
      !!onboardingPendingPrompt ||
      isVerifying ||
      !!paymentUrl;
  }, [
    showChooseExperience,
    showAgreement,
    showRiskAssessment,
    onboardingPendingPrompt,
    isVerifying,
    paymentUrl,
  ]);

  /**
   * Picking Direct charges immediately — no KYC, onboarding or risk-profile
   * check stands in front of Razorpay. The Digio agreement is the only thing
   * that follows the payment; onboarding waits until the customer invests.
   */
  const startDirectPaymentFlow = useCallback(async () => {
    try {
      // Customer type is switched to DIRECT only after the payment succeeds
      await initiatePayment(1, {
        onSuccess: async () => {
          try {
            // useSubscriptionPayment already flips the type to DIRECT on a
            // confirmed payment; make sure it lands even if that call failed.
            if (getCustomerType(onboardingData) !== 'DIRECT') {
              await updateCustomerType({ customerType: 'DIRECT' }).unwrap();
            }
          } catch (err) {
            console.error('Failed to update customer type after payment:', err);
          }

          if (refetchOnboarding) {
            await refetchOnboarding();
          }

          // Straight into signing — a fresh payment always restarts the rest
          // of the journey, even if it was dismissed earlier in this session.
          hasAgreementBeenDismissed.current = false;
          hasRiskBeenDismissed.current = false;
          setShowAgreement(true);
        },
      });
    } catch (err) {
      console.error('Failed to proceed with Direct Plan payment:', err);
    }
  }, [
    onboardingData,
    updateCustomerType,
    refetchOnboarding,
    initiatePayment,
  ]);

  const directJourneyCallbacks = useCallback(
    () => ({
      onPayment: startDirectPaymentFlow,
      // Being sent back to a step outranks having dismissed it earlier —
      // without clearing the ref the watcher would close the modal again.
      onAgreement: () => {
        hasAgreementBeenDismissed.current = false;
        setShowAgreement(true);
      },
      onRiskRequired: () => {
        hasRiskBeenDismissed.current = false;
        setShowRiskAssessment(true);
      },
      // Onboarding is asked for, never forced — same prompt the invest gate raises.
      onOnboardingRequired: () => {
        setOnboardingPendingPrompt({
          pendingStep: getPendingOnboardingStep(onboardingData),
          steps: getOnboardingStepsProgress(onboardingData),
          actionType: 'invest',
        });
      },
    }),
    [startDirectPaymentFlow, onboardingData],
  );

  // Watch onboarding status and drive the plan chooser / journey modals.
  // The chooser no longer waits on onboarding — a customer picks a plan (and
  // pays for Direct) first, and completes the journey later to transact.
  useEffect(() => {
    if (isLoadingOnboarding || isSubLoading || isVerifying || paymentUrl || !onboardingData) return;

    const currentEligibility = getInvestmentEligibility(onboardingData, {
      isSubscriptionActive: isPaidSubscription,
    });

    // Regular / Free customers never see Choose Plan, payment, or Digio.
    if (currentEligibility.isFreeCustomer) {
      setShowChooseExperience(false);
      setShowAgreement(false);
      return;
    }

    const customerType = currentEligibility.customerType;
    const isMutualFundsRoute = location.pathname.startsWith("/mutual-funds");

    if (!customerType) {
      // No plan picked yet: Regular vs Direct, whatever the onboarding state.
      const hasClosed = hasClosedPlanModal.current;
      if (!hasClosed || isMutualFundsRoute) {
        setShowChooseExperience(true);
      }
      setShowAgreement(false);
      return;
    }

    // Plan already chosen — never show the Regular vs Direct chooser again,
    // except while a Direct customer still owes the subscription fee: that
    // chooser is the consent screen the charge has to be started from.
    const isPaymentPending =
      currentEligibility.isDirect && currentEligibility.nextAction === 'payment';
    if (!isPaymentPending) {
      setShowChooseExperience(false);
    }

    // Walk the paid-Direct journey one modal at a time: agreement, then risk.
    // 'onboarding' is never raised here — the invest gate owns that step.
    const step = currentEligibility.isDirect ? currentEligibility.nextAction : null;

    // 'agreement' is reached only once the subscription is paid, so this opens
    // Digio the moment payment lands and keeps it available until it is signed.
    setShowAgreement(step === 'agreement' && !hasAgreementBeenDismissed.current);

    // Risk profiling follows the signature, and is the last thing asked of a
    // Direct customer before they are free to browse.
    setShowRiskAssessment(step === 'risk' && !hasRiskBeenDismissed.current);
  }, [onboardingData, isLoadingOnboarding, isSubLoading, isVerifying, paymentUrl, isPaidSubscription, location.pathname]);

  // Global click interceptor: enforces the plan choice and the paid-Direct
  // steps that precede it (payment → agreement). Onboarding is deliberately
  // NOT enforced here — useInvestGate raises it at invest time instead.
  useEffect(() => {
    const handleGlobalClick = (e) => {
      if (isLoadingOnboarding || isSubLoading || isSubFetching || isVerifying || paymentUrl || !onboardingData) return;

      if (isSubscriptionFree(onboardingData)) return;

      // Let dismiss / backdrop clicks close modals on the first tap.
      if (isJourneyModalOpenRef.current) return;
      if (Date.now() < suppressJourneyEnforcementUntilRef.current) return;

      // Header navigation, the bell and the profile menu never enforce anything.
      const isExemptTarget = e.target.closest(
        '[role="navigation"], [aria-label="notifications"], [aria-label="user account menu"], .MuiPopover-root, .MuiMenu-root'
      );
      if (isExemptTarget) return;

      // Modal surfaces own their own gating (useInvestGate / usePlanGate).
      const isClickInsideModal = e.target.closest('.MuiDialog-root, .MuiDrawer-root, .MuiBackdrop-root');
      if (isClickInsideModal) return;

      const pathname = location.pathname;

      // Someone already inside the journey is left alone.
      if (isOnboardingJourneyRoute(pathname) || isOnboardingExemptRoute(pathname)) return;

      const currentEligibility = getInvestmentEligibility(onboardingData, {
        isSubscriptionActive: isPaidSubscription,
      });
      const customerType = currentEligibility.customerType;
      const nextAction = currentEligibility.nextAction;

      // No plan picked yet: Regular vs Direct.
      const shouldEnforcePlanSelection = !customerType;

      // 'payment' is deliberately absent: a Direct customer who has not paid
      // yet browses freely. Checkout costs money, so it is only ever reached
      // from an explicit action — Invest (useInvestGate), a consent gate
      // (usePlanGate), Continue on the plan chooser, or the Active Plan
      // upgrade button. A stray click anywhere must never open it.
      //
      // The steps after payment only raise a modal, so a plain click may
      // enforce them. 'onboarding' is an invest-time requirement and must
      // never bounce one.
      const shouldEnforceDirectJourney =
        customerType === 'DIRECT' &&
        (nextAction === 'agreement' || nextAction === 'risk');

      if (!shouldEnforcePlanSelection && !shouldEnforceDirectJourney) return;

      if (shouldEnforcePlanSelection) {
        setShowChooseExperience(true);
      } else {
        applyDirectJourneyStepAction(
          nextAction,
          navigate,
          directJourneyCallbacks(),
        );
      }
    };

    window.addEventListener('click', handleGlobalClick);
    return () => {
      window.removeEventListener('click', handleGlobalClick);
    };
  }, [
    onboardingData,
    isLoadingOnboarding,
    isSubscriptionActive,
    isPaidSubscription,
    isSubLoading,
    isSubFetching,
    isVerifying,
    paymentUrl,
    navigate,
    directJourneyCallbacks,
    location.pathname,
  ]);

  // Background polling of onboarding status commented out to prevent unnecessary API load/crashing
  // Polling is handled specifically in DigioAgreementModal when the signing flow is active.
  /*
  useEffect(() => {
    if (isLoadingOnboarding || !onboardingData) return;

    const customerType = onboardingData?.data?.customerType || onboardingData?.data?.customer_type;
    const isDirect = customerType === "DIRECT";
    const agreementCompleted = onboardingData?.data?.agreement?.completed;

    let intervalId;
    if (isDirect && !agreementCompleted) {
      intervalId = setInterval(() => {
        refetchOnboarding();
      }, 5000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [onboardingData, isLoadingOnboarding, refetchOnboarding]);
  */

  // Listen to manual open modal events from click triggers
  useEffect(() => {
    const handleOpenAgreementModal = () => {
      const currentEligibility = getInvestmentEligibility(onboardingData, {
        isSubscriptionActive: isPaidSubscription,
      });
      if (!currentEligibility.isAgreementMandatory) return;
      // An explicit request outranks an earlier dismissal.
      hasAgreementBeenDismissed.current = false;
      setShowAgreement(true);
    };
    const handleOpenPlanModal = () => {
      if (isSubscriptionFree(onboardingData)) return;

      const type = getCustomerType(onboardingData);
      if (type === 'DIRECT') {
        const currentEligibility = getInvestmentEligibility(onboardingData, {
          isSubscriptionActive: isPaidSubscription,
        });

        // Payment is the one step this event must not carry out itself. Some
        // callers raise it from a mount effect rather than a button (the
        // redeem sub-route gates on load, since it is reachable by direct
        // URL), so going straight to Razorpay here opens checkout on a plain
        // page load. Show the plan chooser and let Continue start the charge.
        if (currentEligibility.nextAction === 'payment') {
          setShowChooseExperience(true);
          return;
        }

        applyDirectJourneyStepAction(
          currentEligibility.nextAction,
          navigate,
          directJourneyCallbacks(),
        );
        return;
      }

      // New user — show Regular vs Direct chooser.
      if (!type) {
        setShowChooseExperience(true);
      }
    };
    // Risk profiling is a paid-Direct requirement only — mirror the agreement
    // handler and ignore the event for Regular / Prime / free customers.
    const handleOpenRiskModal = () => {
      const currentEligibility = getInvestmentEligibility(onboardingData, {
        isSubscriptionActive: isPaidSubscription,
      });
      if (!currentEligibility.isRiskAssessmentMandatory) return;
      // An explicit request outranks an earlier dismissal.
      hasRiskBeenDismissed.current = false;
      setShowRiskAssessment(true);
    };
    window.addEventListener('open-agreement-modal', handleOpenAgreementModal);
    window.addEventListener('open-plan-selection-modal', handleOpenPlanModal);
    window.addEventListener('open-risk-assessment-modal', handleOpenRiskModal);
    return () => {
      window.removeEventListener('open-agreement-modal', handleOpenAgreementModal);
      window.removeEventListener('open-plan-selection-modal', handleOpenPlanModal);
      window.removeEventListener('open-risk-assessment-modal', handleOpenRiskModal);
    };
  }, [
    onboardingData,
    isPaidSubscription,
    navigate,
    directJourneyCallbacks,
  ]);

  // The invest gate asks for this prompt instead of navigating on its own.
  // Marking the event handled tells it a prompt is mounted, so it does not
  // fall back to pushing the customer onto the onboarding screen.
  useEffect(() => {
    const handleOnboardingPending = (event) => {
      const detail = event?.detail && typeof event.detail === 'object' ? event.detail : {};

      // The gate re-fetches onboarding status before it blocks, so prefer the
      // detail it carries over this layout's cached copy.
      setOnboardingPendingPrompt({
        pendingStep: detail.pendingStep || getPendingOnboardingStep(onboardingData),
        steps: detail.steps || getOnboardingStepsProgress(onboardingData),
        actionType: detail.actionType || 'invest',
      });

      detail.handled = true;
    };

    window.addEventListener(ONBOARDING_PENDING_MODAL_EVENT, handleOnboardingPending);
    return () => {
      window.removeEventListener(ONBOARDING_PENDING_MODAL_EVENT, handleOnboardingPending);
    };
  }, [onboardingData]);

  const closeOnboardingPendingPrompt = useCallback(() => {
    markJourneyModalDismissed();
    setOnboardingPendingPrompt(null);
  }, [markJourneyModalDismissed]);

  // "Complete" is the only path that moves the customer, and it goes straight
  // to the step they still owe rather than the generic status screen.
  const handleCompleteOnboarding = useCallback(() => {
    const path =
      onboardingPendingPrompt?.pendingStep?.path ||
      ROUTES?.ONBOARDING_STATUS ||
      '/onboarding/status';

    closeOnboardingPendingPrompt();
    navigate(path);
  }, [onboardingPendingPrompt, closeOnboardingPendingPrompt, navigate]);

  const handleChooseExperienceContinue = async (selectedExperience) => {
    if (isFreeCustomer) {
      setShowChooseExperience(false);
      return;
    }

    if (selectedExperience === "fee-based") {
      setShowChooseExperience(false);
      await startDirectPaymentFlow();
    } else {
      try {
        await updateCustomerType({ customerType: "REGULAR" }).unwrap();
        if (refetchOnboarding) {
          await refetchOnboarding();
        }
        setShowChooseExperience(false);
      } catch (err) {
        console.error("Failed to select Regular Plan:", err);
      }
    }
  };

  const handleChooseExperienceClose = () => {
    markJourneyModalDismissed();
    setShowChooseExperience(false);
    hasClosedPlanModal.current = true;

    const isMutualFundsRoute = location.pathname.startsWith("/mutual-funds");

    if (isMutualFundsRoute) {
      navigate(ROUTES.EXPLORE, { replace: true });
    }
  };

  const open = Boolean(menuAnchorEl);
  const userName = user?.fullName ?? user?.name ?? 'User';
  const userEmail = user?.email ?? '';

  const unreadCount = countsData?.counts?.unread ?? 0;

  // Active tab detection logic - simplified without useCallback
  const getActiveTabIndex = (pathname) => {
    for (const group of ROUTE_GROUPS) {
      // Check exact matches first
      if (group.routes.some(route => {
        // Handle dynamic routes with parameters (e.g., /mutual-funds/:fundId)
        if (route.includes(':')) {
          const pattern = route.replace(/:[^/]+/g, '[^/]+');
          const regex = new RegExp(`^${pattern}$`);
          return regex.test(pathname);
        }
        return pathname === route;
      })) {
        return group.tabIndex;
      }

      // Check prefix match if defined
      if (group.matchPrefix) {
        const prefixes = Array.isArray(group.matchPrefix) ? group.matchPrefix : [group.matchPrefix];
        if (prefixes.some(prefix => pathname.startsWith(prefix))) {
          return group.tabIndex;
        }
      }
    }

    // No tab selected for other routes (onboarding, profile, etc.)
    return false;
  };

  // Compute active tab value directly - no state needed
  const tabValue = useMemo(() => {
    return getActiveTabIndex(location.pathname);
  }, [location.pathname]);

  /**
   * Direct customers with onboarding/KYC still pending only get the Home tab.
   * Covers the pre-assignment case too: customerType is null right after login
   * until the plan choice is persisted, so the locally selected plan counts as
   * Direct. Non-Direct types and completed Direct customers see every tab.
   */
  // Existing investors keep their nav (Portfolio included) while a KYC/KRA
  // re-review has onboarding incomplete but the BSE UCC + folios are still valid.
  const { isFetching: isPortfolioFetching, isLoading: isPortfolioLoading } = useGetPortfolioHoldingsQuery(
    { sortBy: 'highest_gain' },
    { skip: isLoadingOnboarding === true || !onboardingData },
  );

  /**
   * Holds the page back only while onboarding/portfolio data is still in flight.
   * Incomplete Direct onboarding does not block navigation - see the one-shot
   * post-login redirect below.
   */
  const shouldGateDirectOnboardingContent = useMemo(() => {
    const pathname = location?.pathname ?? '';

    if (isOnboardingJourneyRoute(pathname)) return false;

    let justLoggedIn = false;
    try {
      justLoggedIn = localStorage?.getItem('justLoggedIn') === 'true';
    } catch {
      justLoggedIn = false;
    }

    if (justLoggedIn && (isLoadingOnboarding === true || !onboardingData)) {
      return true;
    }

    if (isLoadingOnboarding === true || !onboardingData) return false;

    if (
      isUccOnboardingComplete(onboardingData ?? null) === true &&
      isOnboardingComplete(onboardingData ?? null) !== true &&
      (isPortfolioFetching === true || isPortfolioLoading === true)
    ) {
      return true;
    }

    // Incomplete Direct onboarding no longer blocks the page: the tabs are
    // navigable and investing is still gated by useInvestGate/usePlanGate.
    return false;
  }, [
    location?.pathname,
    onboardingData,
    isLoadingOnboarding,
    isPortfolioFetching,
    isPortfolioLoading,
  ]);

  useEffect(() => {
    if (isLoadingOnboarding === true || !onboardingData) return;
    try {
      localStorage?.removeItem('justLoggedIn');
    } catch {
      // ignore storage errors
    }
  }, [isLoadingOnboarding, onboardingData]);

  useEffect(() => {
    if (isLoadingOnboarding === true || !onboardingData) return;
    if (isSubscriptionFree(onboardingData ?? null) === true) return;

    // Wait for portfolio before redirecting re-KYC existing investors who may still transact.
    if (
      isUccOnboardingComplete(onboardingData ?? null) === true &&
      (isPortfolioFetching === true || isPortfolioLoading === true)
    ) {
      return;
    }

    const currentEligibility = getInvestmentEligibility(onboardingData ?? null, {
      isSubscriptionActive: isPaidSubscription ?? false,
    });
    const pathname = location?.pathname ?? '';

    if (currentEligibility?.isFreeCustomer === true) return;

    // An incomplete journey no longer diverts anyone to Account Setup — the
    // customer browses, pays and signs first, and useInvestGate raises
    // onboarding only when they try to transact.

    // A Direct customer whose subscription has lapsed loses the Direct universe.
    // if (
    //   currentEligibility?.isDirect === true &&
    //   isSubscriptionActive !== true &&
    //   pathname?.startsWith?.('/mutual-funds') === true
    // ) {
    //   navigate(ROUTES?.EXPLORE ?? '/explore', { replace: true });
    // }
  }, [
    location?.pathname,
    onboardingData,
    isLoadingOnboarding,
    isSubscriptionActive,
    isPaidSubscription,
    isPortfolioFetching,
    isPortfolioLoading,
    navigate,
  ]);

  const gatedMainContent = shouldGateDirectOnboardingContent === true ? (
    <LoadingFallback fullPage />
  ) : (
    <Outlet />
  );

  // Determine if current route should use full-width content (no side spacing)
  const isFullWidthContent = useMemo(() => {
    return location.pathname.startsWith('/mutual-funds');
  }, [location.pathname]);

  const shouldShowExploreBanner = useMemo(() => {
    return location.pathname === ROUTES.EXPLORE;
  }, [location.pathname]);

  const shouldShowServiceRequest = useMemo(() => {
    return !location.pathname.includes('mutual-funds') &&
      !location.pathname.includes('risk-assessment') &&
      !location.pathname.includes('invest') &&
      !location.pathname.includes('portfolio') &&
      !location.pathname.includes('reports');
  }, [location.pathname]);

  const breadcrumbPadding = useMemo(() => {
    // Universe page (where container has md: 4)
    if (location.pathname.startsWith('/mutual-funds')) {
      return { xs: 2, sm: 4, md: 7 }; // Container(1, 3, 4) + Table(1, 1, 3)
    }
    // Collection details page (where container has md: 0)
    if (location.pathname.includes('/explore/mf-collection-details/')) {
      return { xs: 2, sm: 4, md: 3 }; // Container(1, 3, 0) + Table(1, 1, 3)
    }
    return { xs: 1.5, sm: 2 };
  }, [location.pathname]);

  const showReturnLegend = useMemo(() => {
    return location.pathname === '/mutual-funds' ||
      location.pathname.includes('/explore/mf-collection-details/');
  }, [location.pathname]);

  // The profile tabs carry their own "Profile" title and tab strip, so a
  // "Profile > Personal Details" crumb above them is pure duplication.
  // Standalone profile forms (add nominee/bank, new request) keep theirs.
  const isProfileTabScreen = useMemo(() => {
    return location.pathname === ROUTES.PROFILE ||
      getActiveProfileTabIndex(location.pathname) !== -1;
  }, [location.pathname]);

  const ReturnLegend = () => {
    const legends = [
      { label: 'Poor', color: '#FF4D4D' },
      { label: 'Fair', color: '#FF9433' },
      { label: 'Good', color: '#FFD918' },
      { label: 'Very Good', color: '#99E63E' },
      { label: 'Excellent', color: '#00BE57' },
    ];

    return (
      <Box sx={{
        display: { xs: 'none', md: 'flex' },
        alignItems: 'center',
        gap: 2,
        backgroundColor: '#1C2033',
        border: `1px solid ${theme.palette.custom?.stroke?.divider || alpha('#fff', 0.12)}`,
        borderRadius: 2,
        p: 0.5,
        px: 1.5,
      }}>
        <Tooltip
          title={
            <Box>
              <ul style={{
                margin: 0,
                paddingLeft: '16px',
                fontSize: '13px',
                fontFamily: theme.typography?.fontFamily || 'inherit',
                fontWeight: 400,
                lineHeight: 1.5,
                color: '#E5E7EB'
              }}>
                <li style={{ paddingBottom: '6px' }}>Funds are bucketed on various parameters with respect to other funds in their category.</li>
                <li>Annualized returns are displayed for 1 year and above.</li>
              </ul>
            </Box>
          }
          placement="left"
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: '#2E3545',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                borderRadius: '8px',
                // p: '12px 16px',
                maxWidth: '600px',
                boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
                '& .MuiTooltip-arrow': {
                  color: '#2E3545',
                  "&::before": {
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                  }
                },
              },
            },
          }}
        >
          <InfoOutlined sx={{ fontSize: 18, color: theme.palette.custom?.text?.helperEnabled || '#9CA3AF', cursor: 'pointer' }} />
        </Tooltip>

        {legends.map((item) => (
          <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{
              width: 12,
              height: 12,
              borderRadius: '2px',
              backgroundColor: item.color,
              flexShrink: 0
            }} />
            <Typography sx={{
              fontSize: '11px',
              fontWeight: 500,
              color: theme.palette.custom?.text?.helperEnabled || '#9CA3AF',
              whiteSpace: 'nowrap'
            }}>
              {item.label}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  // Header scroll detection logic - simplified to always fixed
  const [headerHeight, setHeaderHeight] = useState(0);
  const headerRef = useRef(null);

  // Handle header offset for sticky sub-tabs
  useEffect(() => {
    // Always provide the offset when the header is fixed
    const offset = headerHeight;
    document.documentElement.style.setProperty('--header-offset', `${offset}px`);

    return () => {
      document.documentElement.style.removeProperty('--header-offset');
    };
  }, [headerHeight]);

  // Measure header height for spacer
  useEffect(() => {
    const updateHeight = () => {
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.offsetHeight);
      }
    };

    // Initial measure
    updateHeight();

    // Measure on resize
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, [tabValue]); // Re-measure if tabs change (might affect height on mobile)

  // Other functions and logic - memoized with useCallback

  const handleMenuOpen = useCallback((event) => {
    setMenuAnchorEl(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuAnchorEl(null);
  }, []);

  const handleMenuItemClick = useCallback((itemId) => {
    console.log('Menu item clicked:', itemId);
    // Handle menu item actions here
  }, []);

  const handleNotificationsClick = useCallback(() => {
    navigate(ROUTES.NOTIFICATIONS);
  }, [navigate]);

  const handleRefresh = useCallback(async () => {
    console.log('🔄 Pull-To-Refresh: Triggered');

    try {
      // 1. Invalidate all major tags to trigger automatic refetch of active queries.
      // This is the primary mechanism for RTK Query to refresh data across the app.
      //  console.log('🔄 Pull-To-Refresh: Invalidating tags...');
      dispatch(baseApi.util.invalidateTags([
        'Portfolio',
        'InvestmentPortfolio',
        'Notifications',
        'Dashboard',
        'Consents',
        'SIPs',
        'MutualFunds',
        'BankingAccounts',
        'InsurancePolicies'
      ]));

      // 2. Manual refetch fallback for critical queries mounted in this layout.
      // NOTE: refetchOnMountOrArgChange: true does NOT trigger on tag invalidation.
      // We manually call refetch() here to guarantee these core UI elements refresh immediately.
      console.log('🔄 Pull-To-Refresh: Executing manual refetch fallback for layout queries...');
      await Promise.all([
        refetchNotifications().unwrap().catch(err => console.error('Notifications refetch failed:', err)),
        refetchConsents().unwrap().catch(err => console.error('Consents refetch failed:', err))
      ]);

      ////   console.log('✅ Pull-To-Refresh: Data refresh complete');
    } catch (error) {
      console.error('❌ Pull-To-Refresh: Error during refresh:', error);
    }

    // Add a small UX delay so the user sees the 'Refreshing' state for a moment
    await new Promise(resolve => setTimeout(resolve, 800));
  }, [dispatch, refetchNotifications, refetchConsents]);


  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTo(0, 0);
    document.body.scrollTo(0, 0);
    const root = document.getElementById('root');
    if (root) {
      root.scrollTo(0, 0);
    }
    const appScrollContainer = document.getElementById('app-scroll-container');
    if (appScrollContainer) {
      appScrollContainer.scrollTo(0, 0);
    }
  }, [location.pathname]);

  const handleTabChange = useCallback((event, newValue) => {
    // Tab navigation is handled by Link components, but we need this for controlled Tabs
    // The actual navigation happens via the href prop in AppLinkTabComponent
  }, []);

  // Return/rendering
  return (
    <Stack sx={LAYOUT_STYLES.container}>
      {/* Header */}
      {/* Header - Fixed at top */}
      <Stack
        ref={headerRef}
        sx={{
          ...LAYOUT_STYLES.header,
          position: 'sticky',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1100,
          transition: 'box-shadow 0.3s',
          boxShadow: 4, // Always show shadow since it's sticky
        }}
      >
        <Stack sx={LAYOUT_STYLES.headerContent}>
          {/* Top Row: Logo and Actions (for xs) / Full Row (for sm+) */}
          <Stack direction="row" alignItems="center" sx={LAYOUT_STYLES.topRow}>
            {/* Logo */}
            <AppBoxComponent display="flex" sx={LAYOUT_STYLES.logo} onClick={() => navigate(ROUTES.EXPLORE)}>
              <img src={Logo} alt="logo" />
            </AppBoxComponent>

            {/* Navigation Tabs - Inline for md+ screens */}
            <AppBoxComponent
              sx={LAYOUT_STYLES?.navTabsInline ?? {}}
            >
              <NavigationTabsComponent
                value={tabValue}
                onChange={handleTabChange}
                tabs={NAV_TABS}
                ariaLabel="main navigation"
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  minHeight: 'auto',
                  '& .MuiTabs-indicator': {
                    height: 2,
                  },
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    minWidth: { xs: 'auto', md: 72 },
                    paddingX: { xs: 1.5, md: 2 },
                  },
                }}
              />
            </AppBoxComponent>

            <AppBoxComponent sx={LAYOUT_STYLES.spacer} />

            {/* Right side actions */}
            <Stack direction="row" alignItems="center" spacing={1}>
              {/* Notifications */}
              <IconButton
                onClick={handleNotificationsClick}
                aria-label="notifications"
                size="small"
                sx={{
                  color: theme.palette.common.white,
                }}
              >
                <Badge
                  badgeContent={unreadCount}
                  color="error"
                  sx={{
                    '& .MuiBadge-badge': {
                      color: unreadCount > 0 ? theme.palette.common.white : 'transparent',
                      backgroundColor: unreadCount > 0 ? theme.palette.error.main : 'transparent',
                      fontSize: '0.65rem',
                      minWidth: '16px',
                      height: '16px',
                      padding: '0 4px',
                    }
                  }}
                >
                  <NotificationsOutlined fontSize="medium" />
                </Badge>
              </IconButton>

              {/* Profile Avatar - always visible; menu restricted while Direct KYC is pending */}
              <IconButton
                onClick={handleMenuOpen}
                aria-controls={open === true ? 'account-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open === true ? 'true' : undefined}
                aria-label="user account menu"
                size="small"
                sx={{
                  p: 0.5,
                }}
              >
                <Avatar
                  children={getInitials(userName ?? 'User')}
                  variant="circular"
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.background.paper,
                    width: { xs: 32, sm: 36 },
                    height: { xs: 32, sm: 36 },
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  }}
                />
              </IconButton>

              <ProfileMenuComponent
                userName={userName ?? 'User'}
                userEmail={userEmail ?? ''}
                anchorEl={menuAnchorEl ?? null}
                open={open === true}
                onClose={handleMenuClose}
                onMenuItemClick={handleMenuItemClick}
              />
            </Stack>
          </Stack>

          {/* Navigation Tabs - Below top row for xs screens */}
          <AppBoxComponent
            sx={{
              ...(LAYOUT_STYLES?.navTabsContainer ?? {}),
              borderTop: (theme) => ({
                xs: `1px solid ${theme.palette.divider}`,
                sm: 'none',
              }),
            }}
          >
            <NavigationTabsComponent
              value={tabValue}
              onChange={handleTabChange}
              tabs={NAV_TABS}
              ariaLabel="main navigation"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                width: '100%',
                minHeight: 'auto',
                '& .MuiTabs-indicator': {
                  height: 2,
                },
                '& .MuiTab-root': {
                  minHeight: 48,
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  textTransform: 'none',
                  fontWeight: 400,
                  minWidth: { xs: '72px', sm: '120px' }, // Set a sensible min-width
                  paddingX: { xs: 2.5, sm: 3 }, // Increase padding for better touch targets
                  whiteSpace: 'nowrap',
                },
                '& .MuiTabs-scrollButtons': {
                  color: theme.palette.common.white,
                  '&.Mui-disabled': {
                    opacity: 0.3,
                  }
                },
              }}
            />
          </AppBoxComponent>
        </Stack>
      </Stack>


      {/* Main Content */}
      {shouldShowExploreBanner ? (
        <AppBoxComponent component="section" sx={LAYOUT_STYLES.exploreContentWrapper}>
          {!isVideoLoaded && <LoadingFallback fullPage />}
          <AppBoxComponent
            component="video"
            src={exploreBannerVideo}
            autoPlay
            loop
            muted
            playsInline
            controls={false}
            aria-hidden="true"
            onLoadedData={() => setIsVideoLoaded(true)}
            sx={{
              ...LAYOUT_STYLES.exploreBackgroundVideo,
              opacity: isVideoLoaded ? 1 : 0,
              transition: 'opacity 0.6s ease-in-out',
            }}
          />
          <PullToRefreshComponent
            onRefresh={handleRefresh}
            disabled={!(isNative() && location.pathname === '/my-investments')}
          >
            <Stack
              sx={{
                ...LAYOUT_STYLES.exploreContentOverlay,
                opacity: isVideoLoaded ? 1 : 0,
                transition: 'opacity 0.4s ease-in-out',
              }}
            >
              <Suspense fallback={<LoadingFallback fullPage />}>
                {gatedMainContent}
              </Suspense>
            </Stack>
          </PullToRefreshComponent>
          {shouldShowServiceRequest && <ServiceRequestContainer />}
          <ApiHistoryDrawer showOnMobileOnly enabled={false} />
        </AppBoxComponent>
      ) : (
        <>
          <Stack sx={{
            ...LAYOUT_STYLES.mainContent,
            ...(isFullWidthContent ? { maxWidth: '100%', margin: 0 } : {})
          }}>
            {/* Breadcrumb Navigation - Hidden for top-level investment dashboard pages, Reports page and the Profile tabs */}
            {(location.pathname !== ROUTES.INVESTMENTS &&
              location.pathname !== ROUTES.FINANCIAL_OVERVIEW &&
              location.pathname !== ROUTES.REPORTS &&
              !isProfileTabScreen) && (
                <Box sx={{
                  px: breadcrumbPadding,
                  pr: showReturnLegend ? (
                    location.pathname.startsWith('/mutual-funds')
                      ? { xs: 2.5, sm: 4.5, md: 8.5 } // Higher offset for universe page
                      : { xs: 2.5, sm: 4.5, md: 4.5 } // offset for collection details
                  ) : breadcrumbPadding,
                  pt: 1,
                  pb: 0,
                  background: "#1D1D1D3D",
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                }}>
                  <BreadcrumbNavigation />
                  {showReturnLegend && <ReturnLegend />}
                </Box>
              )}

            <PullToRefreshComponent
              onRefresh={handleRefresh}
              disabled={!(isNative() && location.pathname === '/my-investments')}
            >
              <Suspense fallback={<LoadingFallback fullPage />}>
                {gatedMainContent}
              </Suspense>
            </PullToRefreshComponent>
          </Stack>
          {shouldShowServiceRequest && <ServiceRequestContainer />}
          <ApiHistoryDrawer showOnMobileOnly enabled={false} />
        </>
      )}
      <ChooseExperienceModal
        open={showChooseExperience}
        onClose={handleChooseExperienceClose}
        onContinue={handleChooseExperienceContinue}
        userName={userName}
      />
      <DigioAgreementModal
        open={showAgreement}
        onClose={() => {
          markJourneyModalDismissed();
          setShowAgreement(false);
          hasAgreementBeenDismissed.current = true;
        }}
        userName={userName}
      />
      <RiskAssessmentModal
        open={showRiskAssessment}
        onClose={() => {
          markJourneyModalDismissed();
          hasRiskBeenDismissed.current = true;
          setShowRiskAssessment(false);
        }}
      />
      <OnboardingPendingModal
        open={!!onboardingPendingPrompt}
        pendingStep={onboardingPendingPrompt?.pendingStep}
        steps={onboardingPendingPrompt?.steps}
        actionType={onboardingPendingPrompt?.actionType}
        onClose={closeOnboardingPendingPrompt}
        onComplete={handleCompleteOnboarding}
      />
      {/* Global Payment Verification Backdrop Dialog */}
      <Dialog
        open={isVerifying}
        disableEscapeKeyDown
        PaperProps={{
          sx: {
            background: "#13172A",
            borderRadius: "12px",
            p: 4,
            textAlign: "center",
            maxWidth: 400,
          }
        }}
      >
        <DialogContent sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
          <CircularProgress size={40} sx={{ color: "#46A8DC" }} />
          <Typography variant="h6" sx={{ color: "#fff", fontWeight: 600 }}>
            Verifying payment status
          </Typography>
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)" }}>
            {verificationMessage || "Payment in progress. Please wait..."}
          </Typography>
          <AppButtonComponent
            variant="outlined"
            color="error"
            onClick={cancelVerification}
            sx={{ mt: 2, textTransform: "none" }}
          >
            Cancel Verification
          </AppButtonComponent>
        </DialogContent>
      </Dialog>
      {/* Embedded Razorpay Payment Iframe Dialog (Web only) */}
      <Dialog
        open={!!paymentUrl && !isNative()}
        onClose={(event, reason) => {
          if (reason !== 'backdropClick') {
            cancelVerification();
          }
        }}
        disableEscapeKeyDown
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            background: "#13172A",
            borderRadius: "16px",
            height: "80vh",
            maxHeight: 700,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            position: "relative"
          }
        }}
      >
        <IconButton
          onClick={cancelVerification}
          sx={{
            position: 'absolute',
            right: 12,
            top: 12,
            color: '#fff',
            zIndex: 10
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ p: 0, height: "100%", display: "flex", flexDirection: "column" }}>
          {paymentUrl && (
            <iframe
              src={paymentUrl}
              title="Razorpay Subscription Payment"
              style={{
                width: "100%",
                height: "100%",
                border: "none",
                background: "#fff"
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Stack>
  );
};

MainLayout.propTypes = {
  // No props for this component
};

export default memo(MainLayout);